import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { LEAD, idem } from "@/lib/format";
import { StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const REASONS = ["GPS tidak tersedia", "GPS tidak akurat", "Peserta berada di dalam venue"];

function CameraScanner({ onResult }) {
  const [camErr, setCamErr] = useState(false);
  const done = useRef(false);
  useEffect(() => {
    const s = new Html5Qrcode("qr-reader");
    let started = false;
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 }, (text) => {
      if (done.current) return;
      done.current = true;
      onResult(text);
    }, () => {})
      .then(() => { started = true; })
      .catch(() => setCamErr(true));
    return () => {
      if (started) s.stop().then(() => s.clear()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <div id="qr-reader" className="overflow-hidden rounded-xl bg-black/90" data-testid="qr-camera" />
      {camErr && <p className="mt-2 text-xs text-muted-foreground" data-testid="camera-error">Kamera tidak tersedia. Masukkan kode QR secara manual.</p>}
    </div>
  );
}

export function ManualCheckinForm({ eventId, token, onDone }) {
  const [reason, setReason] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/keeper/checkin", { event_id: eventId, token: token || undefined, phone: token ? undefined : phone, reason },
        { headers: { "Idempotency-Key": idem("mc", eventId, token || phone) } });
      toast.success(`${data.participant} berhasil check-in manual.`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-xl bg-[#F5F3EB] p-3" data-testid="manual-checkin-form">
      {!token && <Input placeholder="No. WhatsApp peserta (dewasa)" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11" data-testid="manual-checkin-phone-input" />}
      <div className="flex flex-wrap gap-1.5">
        {REASONS.map((r) => (
          <button key={r} onClick={() => setReason(r)} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold ring-1 ring-[#1A3323]/15 hover:bg-[#EDE9DC]" data-testid={`manual-reason-${REASONS.indexOf(r)}`}>{r}</button>
        ))}
      </div>
      <Textarea placeholder="Alasan check-in manual (wajib)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="manual-checkin-reason-input" />
      <Button className="h-12 w-full rounded-full font-bold" disabled={busy || reason.trim().length < 3 || (!token && !phone)} onClick={submit} data-testid="manual-checkin-submit-button">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Check-in Manual
      </Button>
    </div>
  );
}

const NEXT = { WAITING: ["call", "PANGGIL"], CALLED: ["arrive", "KONFIRMASI HADIR"], ARRIVED: ["start", "MULAI"], CLIMBING: ["complete", "SELESAI"] };

function ScanResult({ r, eventId, onAct, onRefresh }) {
  const t = r.ticket;
  return (
    <div className="space-y-3" data-testid="scan-result">
      <div className="rounded-xl bg-[#1A3323] p-4 text-[#FDFCF8]">
        <div className="text-lg font-extrabold" data-testid="scan-participant-name">{r.participant.display_name}</div>
        <div className="text-xs text-[#FDFCF8]/70">{r.participant.is_child ? "Anak · " : ""}{r.participant.age} th · Lead: {(LEAD[r.participant.lead_status] || LEAD.NOT_VERIFIED).label}</div>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <span className={`rounded-full px-2 py-0.5 font-bold ${r.waiver_ok ? "bg-green-200 text-green-900" : "bg-red-200 text-red-900"}`} data-testid="scan-waiver-status">Waiver {r.waiver_ok ? "OK" : "BELUM"}</span>
          <span className={`rounded-full px-2 py-0.5 font-bold ${r.checked_in ? "bg-green-200 text-green-900" : "bg-amber-200 text-amber-900"}`} data-testid="scan-checkin-status">{r.checked_in ? "Sudah check-in" : "Belum check-in"}</span>
        </div>
      </div>
      {t ? (
        <div className="flex items-center justify-between rounded-xl bg-white p-3 ring-1 ring-[#1A3323]/10" data-testid="scan-ticket">
          <div><div className="font-mono text-2xl font-extrabold">{t.number}</div><div className="text-xs">{t.route_name}</div></div>
          <StatusBadge status={t.status} overdue={t.overdue} />
        </div>
      ) : (
        r.checked_in && <p className="text-sm text-muted-foreground">Tidak ada tiket aktif.</p>
      )}
      {t && NEXT[t.status] && (
        <Button className="h-14 w-full rounded-2xl text-lg font-extrabold" onClick={() => onAct(t.id, NEXT[t.status][0])} data-testid="scan-next-action-button">{NEXT[t.status][1]}</Button>
      )}
      {!r.checked_in && <ManualCheckinForm eventId={eventId} token={r.token} onDone={onRefresh} />}
    </div>
  );
}

export function ScanDialog({ open, onOpenChange, eventId, onAct }) {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [key, setKey] = useState(0);
  const lookup = async (token) => {
    setBusy(true);
    try {
      const { data } = await api.post("/keeper/scan", { event_id: eventId, token });
      setRes({ ...data, token });
    } catch (e) {
      toast.error(errMsg(e));
      setKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };
  const reset = () => { setRes(null); setCode(""); setKey((k) => k + 1); };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[94vh] max-w-md overflow-y-auto" data-testid="scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5" /> Scan QR Peserta</DialogTitle>
          <DialogDescription>Scan tidak otomatis menyelesaikan sesi. Pilih aksi yang sesuai.</DialogDescription>
        </DialogHeader>
        {res ? (
          <>
            <ScanResult r={res} eventId={eventId} onAct={async (id, a) => { await onAct(id, a); lookup(res.token); }} onRefresh={() => lookup(res.token)} />
            <Button variant="outline" className="h-11 rounded-full" onClick={reset} data-testid="scan-again-button">Scan lagi</Button>
          </>
        ) : (
          <>
            {open && <CameraScanner key={key} onResult={lookup} />}
            <div className="flex gap-2">
              <Input placeholder="atau ketik kode QR (IYM:...)" value={code} onChange={(e) => setCode(e.target.value)} className="h-11" data-testid="scan-manual-code-input" />
              <Button className="h-11" disabled={!code || busy} onClick={() => lookup(code)} data-testid="scan-manual-submit-button">Cek</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
